import React, { useState } from 'react';
import {
  Search, Target, BarChart3, Lightbulb, Loader,
  CheckCircle, XCircle
} from 'lucide-react';
import { cmsFetch } from '../lib/cmsApi';

const SEO_SUGGEST_PROMPT = "You are an SEO specialist for Wanderloop, an urban walking platform. Given the SEO audit results for a walk page, suggest 2-3 specific, actionable improvements. Be concise — one sentence each. No numbering, one suggestion per line.";

const SEOAnalysisPanel = ({ walkId }) => {
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const runAnalysis = async () => {
    if (!walkId) return;
    setAnalyzing(true);

    try {
      // Fetch the walk data
      const res = await cmsFetch(`/api/cms/walks/${walkId}`);
      if (!res.ok) { setAnalyzing(false); return; }
      const walk = await res.json();

      // Run checks
      const metaTitle = walk.meta_title || '';
      const metaDesc = walk.meta_description || '';
      const description = walk.description || '';
      const descWords = description.trim().split(/\s+/).filter(Boolean).length;
      const seoTags = walk.seo_tags || [];
      const hasImage = Boolean(walk.main_image);
      const hasRegion = Boolean(walk.region && walk.region !== 'Los Angeles');
      const hasStructuredData = Boolean(walk.meta_title && walk.meta_description);

      const checks = [
        {
          label: 'Meta Title Length',
          pass: metaTitle.length >= 50 && metaTitle.length <= 60,
          value: metaTitle.length ? `${metaTitle.length} chars` : 'Not set',
          tip: metaTitle.length === 0 ? 'Add a meta title in Settings & SEO tab.'
            : metaTitle.length < 50 ? 'Too short — aim for 50-60 characters.'
            : metaTitle.length > 60 ? 'Too long — Google will truncate it. Aim for 50-60 chars.'
            : 'Perfect length.',
          points: 15,
        },
        {
          label: 'Meta Description Length',
          pass: metaDesc.length >= 150 && metaDesc.length <= 160,
          value: metaDesc.length ? `${metaDesc.length} chars` : 'Not set',
          tip: metaDesc.length === 0 ? 'Add a meta description — use the auto-generate button.'
            : metaDesc.length < 150 ? 'Too short — aim for 150-160 characters for full SERP display.'
            : metaDesc.length > 160 ? 'Slightly long — may be truncated in search results.'
            : 'Perfect length.',
          points: 15,
        },
        {
          label: 'Description Word Count',
          pass: descWords >= 100,
          value: `${descWords} words`,
          tip: descWords < 50 ? 'Very thin content. Aim for at least 100 words to rank well.'
            : descWords < 100 ? 'Almost there — add more detail about the route, highlights, or experience.'
            : 'Good content depth.',
          points: 20,
        },
        {
          label: 'SEO Tags',
          pass: seoTags.length >= 5,
          value: `${seoTags.length} tags`,
          tip: seoTags.length === 0 ? 'No tags set — use the auto-suggest button in Settings & SEO.'
            : seoTags.length < 5 ? `Add ${5 - seoTags.length} more tags for better discoverability.`
            : 'Good tag coverage.',
          points: 15,
        },
        {
          label: 'Hero Image',
          pass: hasImage,
          value: hasImage ? 'Present' : 'Missing',
          tip: hasImage ? 'Image set.' : 'Add a main image — walks with photos get 3x more clicks.',
          points: 15,
        },
        {
          label: 'Schema.org Data',
          pass: hasStructuredData,
          value: hasStructuredData ? 'Present' : 'Incomplete',
          tip: hasStructuredData ? 'Meta fields set — structured data will auto-generate.'
            : 'Set both meta title and meta description to enable rich search results.',
          points: 10,
        },
        {
          label: 'Region / Neighborhood',
          pass: hasRegion,
          value: walk.region || 'Not set',
          tip: hasRegion ? 'Region set.' : 'Set a specific neighborhood — "Santa Monica" ranks better than "Los Angeles".',
          points: 10,
        },
      ];

      const totalPoints = checks.reduce((s, c) => s + c.points, 0);
      const earnedPoints = checks.filter(c => c.pass).reduce((s, c) => s + c.points, 0);
      const score = Math.round((earnedPoints / totalPoints) * 100);

      // Get AI suggestions for failures
      let aiSuggestions = [];
      const failures = checks.filter(c => !c.pass);
      if (failures.length > 0) {
        try {
          const failureSummary = failures.map(f => `${f.label}: ${f.value} (${f.tip})`).join('. ');
          const userMsg = `Walk: "${walk.title}", Region: ${walk.region || 'not set'}, SEO issues: ${failureSummary}. Suggest improvements.`;

          const aiRes = await cmsFetch('/api/cms/ai-suggest', {
            method: 'POST',
            body: JSON.stringify({ system_prompt: SEO_SUGGEST_PROMPT, user_message: userMsg }),
          });
          if (aiRes.ok) {
            const aiData = await aiRes.json();
            aiSuggestions = aiData.suggestions || [];
          }
        } catch { /* AI suggestions are optional */ }
      }

      setAnalysis({ checks, score, aiSuggestions, walkTitle: walk.title });
    } catch (e) {
      console.error('SEO analysis failed:', e);
    } finally {
      setAnalyzing(false);
    }
  };

  if (!walkId) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <Search className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">Select a walk to run SEO analysis</p>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-700 bg-green-100';
    if (score >= 70) return 'text-blue-700 bg-blue-100';
    if (score >= 50) return 'text-yellow-700 bg-yellow-100';
    return 'text-red-700 bg-red-100';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <Target className="w-5 h-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">SEO Analysis</h3>
        </div>
        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm"
        >
          {analyzing ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
          {analyzing ? 'Analyzing...' : analysis ? 'Re-analyze' : 'Run SEO Analysis'}
        </button>
      </div>

      <div className="p-6">
        {!analysis ? (
          <div className="text-center py-8">
            <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Click "Run SEO Analysis" to check this walk's search optimization.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Score */}
            <div className="text-center">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold ${getScoreColor(analysis.score)}`}>
                {analysis.score}
              </div>
              <p className="text-lg font-medium text-gray-900 mt-2">SEO Score</p>
              <p className="text-sm text-gray-500">{analysis.walkTitle}</p>
            </div>

            {/* Checks */}
            <div className="space-y-2">
              {analysis.checks.map((check, i) => (
                <div key={i} className={`flex items-start gap-3 p-3 rounded-lg ${check.pass ? 'bg-green-50' : 'bg-red-50'}`}>
                  {check.pass
                    ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${check.pass ? 'text-green-800' : 'text-red-800'}`}>
                        {check.label}
                      </span>
                      <span className={`text-xs font-mono ${check.pass ? 'text-green-600' : 'text-red-500'}`}>
                        {check.value}
                      </span>
                    </div>
                    {!check.pass && (
                      <p className="text-xs text-red-600 mt-0.5">{check.tip}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* AI Suggestions */}
            {analysis.aiSuggestions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <Lightbulb className="w-4 h-4 mr-1.5 text-amber-500" />
                  AI Recommendations
                </h4>
                <div className="space-y-2">
                  {analysis.aiSuggestions.map((s, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                      <span className="text-amber-500 mt-0.5">•</span>
                      <p className="text-sm text-amber-800">{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOAnalysisPanel;
